import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Badge,
  Container,
  Table,
  Button,
  TabContent,
  TabPane,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import BellRingOutlineIcon from 'mdi-react/BellRingOutlineIcon';
import TableEditIcon from 'mdi-react/TableEditIcon';
import CheckIcon from 'mdi-react/CheckIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import { toast } from 'react-toastify';
import Api from '../../util/api';
import skeletonTableLoader from '../../shared/components/custom/SkeletonLoaders';
import useConfig from '../../util/useConfig';
import Pagination from '../../shared/components/pagination/Pagination';
import t, { partial } from '../../util/translation/translation';
import MatchCenterHelpers from './helpers';
import useModal from '../../shared/hooks/useModal';
import DeleteModal from '../../shared/components/custom/DeleteModal';
import Loading from '../../shared/components/custom/Loading';
import NavTab from '../../shared/components/custom/NavTab';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';
import FormCard from '../../shared/components/custom/FormCard';
import PrizesCard from './PrizesCard';
import MvpLeaderboard from './MvpLeaderboard';
import BulkEditLeagueModal from './BulkEditLeagueModal';

const UpcomingMatches = () => {
  const history = useHistory();
  const location = useLocation();
  const config = useConfig();
  const s = partial('shared');
  const mc = partial('matchCenter');
  const lm = partial('ListMatches');
  const [tab, setTab] = useState('1');
  const [matchFilter, setMatchFilter] = useState('upcoming');
  const [handleModal, visible, deleteId] = useModal();
  const { addDash, getCompany, getSponsor } = MatchCenterHelpers;
  const [matches, setMatches] = useState([]);
  const [concludedMatches, setConcludedMatches] = useState([]);
  const [paginationDetails, setPaginationDetails] = useState({
    first: 1,
    last: 1,
    previous: null,
    next: null,
    total: 0,
  });
  const [paginationDetailsConcluded, setPaginationDetailsConcluded] = useState({
    first: 1,
    last: 1,
    previous: null,
    next: null,
    total: 0,
  });
  // Read the starting page from the URL so that navigating away to edit a
  // match and coming back (e.g. via the browser/history back action)
  // restores the page the user was on, instead of always resetting to 1.
  const initialParams = new URLSearchParams(location.search);
  const [currentPage, setCurrentPage] = useState(
    Number(initialParams.get('page')) || 1
  );
  // Note: the shared Pagination component always seeds its page size from
  // rowsPerPage[0] on mount (it has no prop to restore a chosen size), so
  // this only matters until the user changes the page size themselves.
  const [pageSize, setPageSize] = useState(
    Number(initialParams.get('pageSize')) || 10
  );
  const [concludedMatchesCurrentPage, setConcludedMatchesCurrentPage] =
    useState(Number(initialParams.get('concludedPage')) || 1);
  const [concludedMatchespageSize, setConcludedMatchesPageSize] = useState(
    Number(initialParams.get('concludedPageSize')) || 10
  );
  const {
    data: upcomingMatches,
    isLoading,
    refetch: refecthUpcoming,
  } = useQuery({
    queryKey: ['upcoming-matches-banner', currentPage, pageSize],
    queryFn: () =>
      Api.matchCenter.getUpcomingMatches(
        currentPage,
        pageSize,
        config.teams.map((team) => team.id),
        false
      ),
  });
  const {
    data: concludedMatchesData,
    isLoading: isLoadingConcluded,
    refetch: refecthConcluded,
  } = useQuery({
    queryKey: [
      'concluded-matches-banner',
      concludedMatchesCurrentPage,
      concludedMatchespageSize,
    ],
    queryFn: () =>
      Api.matchCenter.getUpcomingMatches(
        concludedMatchesCurrentPage,
        concludedMatchespageSize,
        config.teams.map((team) => team.id),
        true
      ),
  });
  const [loading, setLoading] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const { data: companies } = useQuery({
    queryKey: ['club-companies'],
    queryFn: Api.companies.getCompaniesForClub,
  });

  useEffect(() => {
    if (!upcomingMatches) return;
    const results = upcomingMatches.data;
    setMatches(results.matches);
    setPaginationDetails({
      first: results.first,
      last: results.last,
      previous: results.previous,
      next: results.next,
      total: results.total,
    });
  }, [upcomingMatches, isLoading]);

  useEffect(() => {
    if (!concludedMatchesData) return;
    const results = concludedMatchesData.data;
    setConcludedMatches(results.matches);
    setPaginationDetailsConcluded({
      first: results.first,
      last: results.last,
      previous: results.previous,
      next: results.next,
      total: results.total,
    });
  }, [concludedMatchesData, isLoadingConcluded]);

  const handlePagination = (paginationObject, isConcluded) => {
    const params = new URLSearchParams(location.search);
    if (isConcluded) {
      setConcludedMatchesCurrentPage(paginationObject.currentPage);
      if (concludedMatchespageSize !== paginationObject.pageSize) {
        setConcludedMatchesPageSize(paginationObject.pageSize);
      }
      params.set('concludedPage', paginationObject.currentPage);
      params.set('concludedPageSize', paginationObject.pageSize);
    } else {
      setCurrentPage(paginationObject.currentPage);
      if (pageSize !== paginationObject.pageSize) {
        setPageSize(paginationObject.pageSize);
      }
      params.set('page', paginationObject.currentPage);
      params.set('pageSize', paginationObject.pageSize);
    }
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const renderTeam = (match, type) => {
    const name = match.isCustom ? match.extraData[type] : match[type]?.name;
    const logoUrl = match.isCustom
      ? match.extraData[`${type}Logo`]
      : match[type]?.logoUrl;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 22,
            height: 22,
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 5,
            background: logoUrl ? 'transparent' : '#eef0f3',
          }}
        >
          {logoUrl && (
            <img
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
              }}
              src={logoUrl}
              alt={name}
            />
          )}
        </div>
        <span style={{ fontWeight: 500 }}>{name}</span>
      </div>
    );
  };

  const renderSpinner = skeletonTableLoader(3, 10, 100);

  const renderTableRows = (renderMatches) => {
    return renderMatches.map((match) => (
      <tr key={match.id}>
        <td className="table-stack__primary">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 15 }}>
              {moment.utc(match.matchStart).local(true).format('DD MMM')}
            </span>
            <small className="text-muted">
              {moment.utc(match.matchStart).local(true).format('ddd HH:mm')}
            </small>
          </div>
        </td>
        <td className="table-stack__full">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <small
              className="text-muted"
              style={{
                textTransform: 'uppercase',
                fontSize: 10.5,
                letterSpacing: '.04em',
              }}
            >
              {match?.league?.name || match?.extraData?.matchType}
            </small>
            {renderTeam(match, 'homeTeam')}
            <div
              className="text-muted"
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: '.04em',
                paddingLeft: 30,
              }}
            >
              {s('vs')}
            </div>
            {renderTeam(match, 'awayTeam')}
          </div>
        </td>
        <td data-label={mc('matchSponsor')}>
          {addDash(getSponsor(match).name.label)}
        </td>
        <td data-label={mc('pRSponsor')}>
          {addDash(getCompany(match, companies).label)}
        </td>
        <td data-label={mc('ticketLink')}>
          <div className="status-dot-group">
            <span
              className={`status-dot ${match?.settings?.settings?.ticket?.link ? 'status-dot--on' : 'status-dot--off'}`}
            >
              {match?.settings?.settings?.ticket?.link ? (
                <CheckIcon />
              ) : (
                <CloseIcon />
              )}
            </span>
          </div>
        </td>
        <td data-label={mc('bannerVisibility')}>
          <Badge
            color={match?.settings?.visibleInBanner ? 'success' : 'danger'}
          >
            {match?.settings?.visibleInBanner
              ? lm('Visible')
              : lm('NotVisible')}
          </Badge>
        </td>
        <td data-label={mc('origin')}>
          <Badge pill color={match?.isCustom ? 'success' : 'info'}>
            {match?.isCustom ? mc('userCreated') : mc('fetched')}
          </Badge>
        </td>
        <td className="table-stack__actions">
          <div className="icon-actions">
            <SquareIconButton
              icon={<EditOutlineIcon />}
              onClick={() => history.push(`/matches/upcoming/edit/${match.id}`)}
              title={s('edit')}
              id={`editBtn-${match.id}`}
            />
            {/* Push messages live on the edit page's communication tab,
                which isn't available for custom matches. */}
            {!match?.isCustom && (
              <SquareIconButton
                icon={<BellRingOutlineIcon />}
                onClick={() =>
                  history.push(`/matches/upcoming/edit/${match.id}?tab=2`)
                }
                title={s('sendPushNotification')}
                id={`pushBtn-${match.id}`}
              />
            )}
            <SquareIconButton
              icon={<DeleteOutlineIcon />}
              onClick={() => handleModal(match.id)}
              title={s('delete')}
              tone="danger"
              id={`deleteBtn-${match.id}`}
            />
          </div>
        </td>
      </tr>
    ));
  };

  const noRecords = (
    <tr>
      <td colSpan={8} style={{ textAlign: 'center' }}>
        {mc('noRecords')}
      </td>
    </tr>
  );

  const tableLayout = (renderMatches, pagination, _loading, isConcluded) => {
    return (
      <>
        <FormCard
          title={
            <>
              <div className="form-card__switch d-none d-md-inline-flex">
                <button
                  type="button"
                  className={`form-card__switch-btn${
                    !isConcluded ? ' active' : ''
                  }`}
                  onClick={() => setMatchFilter('upcoming')}
                >
                  {lm('comingMatches')}
                </button>
                <button
                  type="button"
                  className={`form-card__switch-btn${
                    isConcluded ? ' active' : ''
                  }`}
                  onClick={() => setMatchFilter('concluded')}
                >
                  {lm('earlierMatches')}
                </button>
              </div>
              <span className="d-md-none">
                {isConcluded ? lm('earlierMatches') : lm('comingMatches')}
              </span>
            </>
          }
          actions={
            <>
              {!isConcluded && (
                <div
                  className="d-none d-md-flex align-items-center"
                  style={{ gap: 8 }}
                >
                  <SquareIconButton
                    icon={<TableEditIcon />}
                    title={mc('bulkEditLeagueMatches')}
                    id="bulk-edit-league"
                    onClick={() => setBulkEditOpen(true)}
                  />
                  <Link to={{ pathname: '/matches/upcoming/custom/create' }}>
                    <Button color="primary" size="sm" className="mb-0">
                      {mc('createMatchBanner')}
                    </Button>
                  </Link>
                </div>
              )}
              <UncontrolledDropdown className="d-md-none">
                <DropdownToggle
                  tag="button"
                  type="button"
                  className="square-icon-btn"
                  caret={false}
                >
                  <DotsHorizontalIcon />
                </DropdownToggle>
                <DropdownMenu right className="dropdown-menu--modern">
                  <DropdownItem
                    active={!isConcluded}
                    onClick={() => setMatchFilter('upcoming')}
                  >
                    {lm('comingMatches')}
                  </DropdownItem>
                  <DropdownItem
                    active={isConcluded}
                    onClick={() => setMatchFilter('concluded')}
                  >
                    {lm('earlierMatches')}
                  </DropdownItem>
                  {!isConcluded && (
                    <>
                      <DropdownItem divider />
                      <DropdownItem onClick={() => setBulkEditOpen(true)}>
                        {mc('bulkEditLeague')}
                      </DropdownItem>
                      <DropdownItem
                        tag={Link}
                        to={{ pathname: '/matches/upcoming/custom/create' }}
                      >
                        {mc('createMatchBanner')}
                      </DropdownItem>
                    </>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            </>
          }
          bodyClassName="form-card__body--flush"
          className="mb-3"
        >
          <Table responsive striped className="table--roomy table--stack">
            <thead>
              <tr>
                <th scope="row">{mc('startDate')}</th>
                <th scope="row">{s('match')}</th>
                <th scope="row">{mc('matchSponsor')}</th>
                <th scope="row">{mc('pRSponsor')}</th>
                <th scope="row">{mc('ticketLink')}</th>
                <th scope="row">{mc('bannerVisibility')}</th>
                <th scope="row">{mc('origin')}</th>
                <th scope="row" />
              </tr>
            </thead>
            <tbody>
              {_loading && renderSpinner}
              {renderMatches.length > 0 && renderTableRows(renderMatches)}
              {!_loading && renderMatches.length === 0 && noRecords}
            </tbody>
          </Table>
        </FormCard>
        <Pagination
          items={pagination.total || 0}
          rowsPerPage={[10, 20, 30]}
          initialPage={isConcluded ? concludedMatchesCurrentPage : currentPage}
          onChangePage={(paginationObject) =>
            handlePagination(paginationObject, isConcluded)
          }
        />
      </>
    );
  };

  const deleteMatch = async () => {
    setLoading(true);
    try {
      await Api.matchCenter.deleteMatch(deleteId);
      toast.success(mc('matchDeleted'));
    } catch (err) {
      toast.error(err);
    }
    handleModal();
    setLoading(false);
    refecthConcluded();
    refecthUpcoming();
  };

  return (
    <Container>
      <NavTab
        activeTab={tab}
        tabs={[s('matches'), t('prizes.prizes'), t('mvpLeaderboard.tab')]}
        setTab={(activeTab) => setTab(activeTab)}
      />
      <Loading loading={loading} />
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type={lm('theMatch')}
        modalAction={deleteMatch}
      />
      <BulkEditLeagueModal
        isOpen={bulkEditOpen}
        toggle={() => setBulkEditOpen(false)}
        companies={companies}
        onDone={() => {
          refecthUpcoming();
          refecthConcluded();
        }}
      />
      <TabContent activeTab={tab}>
        <TabPane tabId="1">
          {matchFilter === 'upcoming'
            ? tableLayout([...matches], paginationDetails, isLoading, false)
            : tableLayout(
                [...concludedMatches],
                paginationDetailsConcluded,
                isLoadingConcluded,
                true
              )}
        </TabPane>
        <TabPane tabId="2">
          <PrizesCard />
        </TabPane>
        <TabPane tabId="3">{tab === '3' && <MvpLeaderboard />}</TabPane>
      </TabContent>
    </Container>
  );
};
export default UpcomingMatches;
